import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true,
  timeout: 30000
});

// Request interceptor (cookies are sent automatically via withCredentials)
apiClient.interceptors.request.use(
  (config) => {
    // Cookie authentication is handled automatically
    // No need to add Authorization header manually
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect if we're already on login page or during auth operations
      const currentPath = window.location.pathname;
      const isAuthEndpoint = error.config?.url?.includes('/api/auth/');
      
      if (currentPath !== '/login' && !isAuthEndpoint) {
        console.log('🔒 Session expired, redirecting to login');
        // Session expired or invalid, redirect to login
        window.location.href = '/login';
      } else {
        console.log('⚠️ Auth error on login page or auth endpoint, not redirecting');
      }
    }
    
    // Handle 402 Payment Required (upgrade needed)
    if (error.response?.status === 402) {
      const upgradeData = error.response?.data;
      // Dispatch custom event for upgrade modal
      window.dispatchEvent(new CustomEvent('upgrade-required', { 
        detail: { 
          feature: upgradeData?.feature || 'default',
          message: upgradeData?.message 
        } 
      }));
    }
    
    return Promise.reject(error);
  }
);

export default apiClient;

// API functions
export const authAPI = {
  register: (data) => apiClient.post('/api/auth/register', data),
  login: (data) => apiClient.post('/api/auth/login', data),
  createSession: (sessionId) => apiClient.post('/api/auth/session', null, {
    headers: { 'X-Session-ID': sessionId }
  }),
  getMe: () => apiClient.get('/api/auth/me'),
  logout: () => apiClient.post('/api/auth/logout')
};

export const userAPI = {
  updateProfile: (data) => apiClient.put('/api/users/profile', data)
};

export const vehicleAPI = {
  list: () => apiClient.get('/api/vehicles'),
  get: (id) => apiClient.get(`/api/vehicles/${id}`),
  create: (data) => apiClient.post('/api/vehicles', data),
  update: (id, data) => apiClient.put(`/api/vehicles/${id}`, data),
  delete: (id) => apiClient.delete(`/api/vehicles/${id}`)
};

export const documentAPI = {
  list: (vehicleId) => apiClient.get(`/api/vehicles/${vehicleId}/documents`),
  upload: (vehicleId, formData) => apiClient.post(`/api/vehicles/${vehicleId}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  download: (documentId) => apiClient.get(`/api/documents/${documentId}/download`, {
    responseType: 'blob'
  }),
  delete: (documentId) => apiClient.delete(`/api/documents/${documentId}`)
};

export const reminderAPI = {
  list: (vehicleId) => apiClient.get('/api/reminders', { params: { vehicle_id: vehicleId } }),
  create: (data) => apiClient.post('/api/reminders', data),
  update: (id, data) => apiClient.put(`/api/reminders/${id}`, data),
  delete: (id) => apiClient.delete(`/api/reminders/${id}`)
};

export const expenseAPI = {
  list: (params) => apiClient.get('/api/expenses', { params }),
  create: (data) => apiClient.post('/api/expenses', data),
  getSummary: (params) => apiClient.get('/api/expenses/summary', { params })
};

export const maintenanceAPI = {
  list: (vehicleId) => apiClient.get('/api/maintenance', { params: { vehicle_id: vehicleId } }),
  create: (data) => apiClient.post('/api/maintenance', data)
};

export const accidentAPI = {
  list: (vehicleId) => apiClient.get('/api/accidents', { params: { vehicle_id: vehicleId } }),
  get: (reportId) => apiClient.get(`/api/accidents/${reportId}`),
  create: (data) => apiClient.post('/api/accidents', data),
  uploadPhoto: (reportId, formData) => apiClient.post(`/api/accidents/${reportId}/photos`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadVideo: (reportId, formData) => apiClient.post(`/api/accidents/${reportId}/video`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  generatePDF: (reportId) => apiClient.post(`/api/accidents/${reportId}/generate-pdf`),
  getPhoto: (reportId, path) => apiClient.get(`/api/accidents/${reportId}/photos/${path}`, {
    responseType: 'blob'
  })
};

export const mileageAPI = {
  // Backend route is /api/mileage-logs (not /api/mileage)
  list: () => apiClient.get('/api/mileage-logs'),
  create: (data) => apiClient.post('/api/mileage-logs', data)
};

export const dashboardAPI = {
  get: () => apiClient.get('/api/dashboard')
};
